import { ReactElement, useMemo, useState } from "react";

import {
    LoopExpanded,
    LoopPositionExpanded,
    BsMetaUtil,
    useFlashPerps,
    isLstLoop,
    calculateOracleProfitLossStats,
    getLeverageLoopDirection,
    LoopFilterTags,
    LoopRouteType,
    Protocol,
    TokenListMetadata,
    isLeverageLoop,
    getZcCollateral
} from "@bridgesplit/abf-react";
import { LOOP_DETAIL_SLUG } from "app/constants";
import {
    Column,
    OutlinedCard,
    Row,
    StatColumn,
    StatProps,
    Text,
    TextButton,
    Tooltip,
    TooltipText,
    useAppPalette,
    Icon,
    TextVariant,
    TagText,
    Span,
    TextColor
} from "@bridgesplit/ui";
import { formatPercent, formatUsd } from "@bridgesplit/utils";
import { NorthOutlined, SouthOutlined, SwapHoriz } from "@mui/icons-material";

import { TokenSolscanLink } from "./asset";
import { SocialShareCta, useLoopSocialShareProps } from "../portfolio";
import { useLoanOraclePriceQuotes } from "./loans";

export enum LoopDisplayType {
    UsdCostBasis,
    TokenCostBasis
}

export function getLoopPath(loopExpanded: LoopExpanded) {
    return `${LOOP_DETAIL_SLUG}/${loopExpanded.vaultIdentifier}`;
}

export function LoopNewTag({ isLoading }: { isLoading?: boolean }) {
    return (
        <TagText textVariant="body2" borderRadius={100} color="secondary" loading={isLoading} loadingWidth="40px">
            New
        </TagText>
    );
}
export function useLoopWindAlerts(loopExpanded: LoopExpanded | undefined) {
    const routeType = loopExpanded?.loopVault.routeType;
    let windDisabledWithMessage: string | undefined;

    const { data: flashPerps } = useFlashPerps({ skip: !loopExpanded || routeType !== LoopRouteType.Flash });

    if (!loopExpanded) return { windDisabledWithMessage };

    if (flashPerps && routeType === LoopRouteType.Flash) {
        const maxAumUsd = loopExpanded?.loopVault.aumLimit;
        const aumUsd =
            flashPerps.pools.find((pool) =>
                loopExpanded.collateralToken.symbol.toLocaleLowerCase().includes(pool.flpTokenSymbol.toLowerCase())
            )?.aum ?? undefined;
        const isCapExceeded = maxAumUsd && aumUsd && maxAumUsd < parseFloat(aumUsd);
        if (isCapExceeded) {
            windDisabledWithMessage = "Deposits temporarily disabled due to Flash Trade's AUM cap being exceeded";
        }
    }

    return { windDisabledWithMessage };
}

export function useLoopUnwindAlerts({
    loopPositionExpanded,
    variant
}: {
    loopPositionExpanded: LoopPositionExpanded | undefined;
    variant?: "preview" | "route";
}) {
    const routeType = loopPositionExpanded?.loopExpanded?.loopVault.routeType;
    let unwindWarningMessage: string | undefined;
    const pnlStats = loopPositionExpanded?.loanExpanded?.pnlStats;

    const { principalUsdOraclePrice, collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loopPositionExpanded?.loopExpanded?.principalToken,
        getZcCollateral(loopPositionExpanded?.loanExpanded)
    );
    if (
        !loopPositionExpanded ||
        pnlStats === undefined ||
        !loopPositionExpanded.loopExpanded ||
        pnlStats?.usdPnl === undefined
    )
        return { unwindWarningMessage };

    const {
        loanExpanded: { netApyLive }
    } = loopPositionExpanded;

    const isTemporarilyNegativePnl = !!netApyLive && netApyLive > 0 && pnlStats?.usdPnl < 0;

    const oraclePnl = calculateOracleProfitLossStats(
        loopPositionExpanded,
        principalUsdOraclePrice,
        collateralUsdOraclePrice
    );

    if (
        isLstLoop(loopPositionExpanded.loopExpanded) &&
        pnlStats?.usdPnl < 0 &&
        oraclePnl &&
        oraclePnl.usdProfitLoss > 0
    ) {
        const stakeWarning =
            "Staking yield compounds with each epoch. The amount you receive reflects the LST's market value, which may not yet include the current epoch's rewards.";

        if ((variant === "route" && isTemporarilyNegativePnl) || variant === "preview") {
            unwindWarningMessage = stakeWarning;
        }
    }

    if (routeType === LoopRouteType.Meteora) {
        if (isTemporarilyNegativePnl) {
            const loopApy = formatPercent(netApyLive);
            if (variant === "route") {
                unwindWarningMessage = `You're earning ${loopApy} APY. Closing now locks in losses. Meteora may not have updated trading fees`;
            } else {
                unwindWarningMessage = `You're earning ${loopApy} APY but your P&L is temporarily negative since Meteora has a setup fee and periodically updates yields`;
            }
        }
    }

    return { unwindWarningMessage };
}

export function LoopTokenUsdToggle({
    usdValue,
    tokenValue,
    tokenMetadata,
    pctChangeToken,
    pctChangeUsd,
    enableSocialShare = false,
    loopPosition,
    togglePosition = "left",
    textVariant = "body1"
}: {
    usdValue: number | undefined;
    tokenValue: number | undefined;
    pctChangeUsd?: number;
    pctChangeToken?: number;
    tokenMetadata: TokenListMetadata | undefined;
    enableSocialShare?: boolean;
    loopPosition?: LoopPositionExpanded | undefined;
    togglePosition?: "left" | "right";
    textVariant?: TextVariant;
}) {
    const { error, success, textDisabled } = useAppPalette();
    const [displayType, setDisplayType] = useState<LoopDisplayType>(
        usdValue ? LoopDisplayType.UsdCostBasis : LoopDisplayType.TokenCostBasis
    );

    const selectedValue = displayType === LoopDisplayType.UsdCostBasis ? usdValue : tokenValue;

    const { icon, color } = useMemo(() => {
        if (!selectedValue) return { icon: null, color: "textDisabled" as TextColor };
        if (selectedValue > 0) return { icon: <NorthOutlined />, color: success };
        if (selectedValue < 0) return { icon: <SouthOutlined />, color: error };
        return { icon: null, color: textDisabled };
    }, [error, selectedValue, success, textDisabled]);

    const socialShareData = useLoopSocialShareProps(loopPosition, displayType);

    if (usdValue === undefined && tokenValue === undefined) {
        return <Text color="disabled">{""}</Text>;
    }

    // Percentage should not change on toggle - use token % for non-leverage and USD % for leverage
    const isLeverage = isLeverageLoop(loopPosition?.loopExpanded);
    const pctChange = isLeverage ? pctChangeUsd : pctChangeToken;

    function PercentComponent() {
        return (
            <Text
                variant="caption"
                sx={{
                    gap: 0.5,
                    color,
                    svg: { fontSize: `12px` }
                }}
            >
                {icon}
                {pctChange !== undefined && formatPercent(pctChange / 100)}
            </Text>
        );
    }

    function ToggleComponent() {
        return (
            <>
                {tokenValue && usdValue && (
                    <TextButton
                        color="disabled"
                        onClick={() =>
                            setDisplayType(
                                displayType === LoopDisplayType.UsdCostBasis
                                    ? LoopDisplayType.TokenCostBasis
                                    : LoopDisplayType.UsdCostBasis
                            )
                        }
                    >
                        <SwapHoriz />
                    </TextButton>
                )}
                {pctChange && <PercentComponent />}
            </>
        );
    }

    return (
        <Row spacing={0.5}>
            {togglePosition === "left" && <ToggleComponent />}
            {displayType === LoopDisplayType.UsdCostBasis && usdValue && (
                <Text translate="no" variant={textVariant}>
                    {formatUsd(usdValue)}{" "}
                </Text>
            )}
            {displayType === LoopDisplayType.TokenCostBasis && tokenValue && (
                <Text translate="no" variant={textVariant}>
                    {BsMetaUtil.formatAmount(tokenMetadata, tokenValue, { shortSymbol: true })}
                </Text>
            )}
            {togglePosition === "right" && <ToggleComponent />}
            {enableSocialShare && socialShareData && <SocialShareCta data={socialShareData} />}
        </Row>
    );
}

export const ORACLE_DISCREPANCY_THRESHOLD = 0.05;

export function OracleTooltipContent({
    collateralToken,
    oracleDiff,
    isWind,
    variant
}: {
    collateralToken: TokenListMetadata | undefined;
    oracleDiff: number;
    isWind: boolean;
    variant: TextVariant;
}) {
    const symbol = BsMetaUtil.getSymbol(collateralToken);

    const getStartingMessage = () => {
        if (oracleDiff > 0) {
            return `The market price is trading higher than the ${symbol}'s underlying value. `;
        } else {
            return `The market price is trading lower than the ${symbol}'s underlying value. `;
        }
    };

    const getPnLMessage = () => {
        if (isWind) {
            return oracleDiff > 0 ? "This may lead to decreased profits." : "This may lead to increased profits.";
        } else {
            return oracleDiff > 0 ? "This may lead to increased profits." : "This may lead to decreased profits.";
        }
    };

    return (
        <Column spacing={2} padding={1} sx={{ width: "fit-content" }}>
            <Text variant={variant} color="caption">
                {getStartingMessage() + getPnLMessage()}
            </Text>
        </Column>
    );
}

export function PendingLeverageStatsCard({
    title,
    helpText,
    stats,
    variant = "body2",
    loading = false
}: {
    title: string;
    helpText: string;
    stats: StatProps[];
    variant?: TextVariant;
    loading?: boolean;
}) {
    const { primaryToAlpha } = useAppPalette();

    return (
        <OutlinedCard hideBorder backgroundColor={primaryToAlpha}>
            <Column spacing={0.5}>
                <TooltipText helpText={helpText} variant={variant} color="caption" loading={loading}>
                    {title}
                </TooltipText>
                <StatColumn variant={variant} stats={stats} loading={loading} />
            </Column>
        </OutlinedCard>
    );
}

export function TokenUsdStat({
    usdValue,
    token,
    usdPrice,
    displayAsColumn,
    warning
}: {
    usdValue: number;
    usdPrice: number;
    token: TokenListMetadata | undefined;
    displayAsColumn?: boolean;
    warning?: string;
}) {
    const tokenAmount = usdValue / usdPrice;
    const amount = BsMetaUtil.formatAmount(token, tokenAmount, { hideSymbol: false, shortSymbol: true });

    return (
        <StatWithSubtext
            stat={formatUsd(usdValue)}
            subtext={amount}
            displayAsColumn={displayAsColumn}
            warning={warning}
        />
    );
}

export function TokenStat({
    tokenAmount,
    token,
    usdPrice,
    displayAsColumn,
    warning
}: {
    tokenAmount: number;
    token: TokenListMetadata | undefined;
    usdPrice: number;
    displayAsColumn?: boolean;
    warning?: string;
}) {
    const amount = BsMetaUtil.formatAmount(token, tokenAmount, { hideSymbol: false });
    const usdValue = formatUsd(tokenAmount * usdPrice);

    return <StatWithSubtext stat={usdValue} subtext={amount} displayAsColumn={displayAsColumn} warning={warning} />;
}

export function StatWithSubtext({
    stat,
    subtext,
    statVariant = "h3",
    subtextVariant = "body2",
    displayAsColumn,
    warning
}: {
    stat: string | JSX.Element;
    subtext?: string;
    statVariant?: "h3" | "h4";
    subtextVariant?: "body2" | "caption";
    displayAsColumn?: boolean;
    warning?: string;
}) {
    const baseStat =
        typeof stat === "string" ? (
            <Text variant={statVariant} svgColor={warning ? "warning" : undefined}>
                {stat}
                {warning && <Icon type="warning" />}
            </Text>
        ) : (
            stat
        );

    const statWrapped = warning ? <Tooltip title={warning}>{baseStat}</Tooltip> : baseStat;

    const content = (
        <>
            {statWrapped}
            {subtext && (
                <Text variant={subtextVariant} color="caption">
                    {subtext}
                </Text>
            )}
        </>
    );

    return displayAsColumn ? <Row spacing={1}>{content}</Row> : <Column spacing={0}>{content}</Column>;
}

export function getLoopDescription(loopExpanded: LoopExpanded | undefined): React.ReactElement | undefined {
    if (!loopExpanded) return undefined;

    const { tags } = loopExpanded.loopVault;
    const { collateralToken, principalToken } = loopExpanded;
    const defaultDescription = (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />
        </Span>
    );

    const leverageDescription = getLeverageLoopDescription(collateralToken, principalToken, loopExpanded);
    if (leverageDescription) return leverageDescription;

    // Hardcoded description for tesouro-usdc
    if (loopExpanded.vaultIdentifier === "tesouro-usdc") {
        return (
            <Span>
                This Loop gives leveraged long exposure to <TokenSolscanLink token={loopExpanded.collateralToken} />.
                The Loop is subject to USDBRL FX risk, meaning an increase in BRL price compounds returns and a decrease
                in BRL price can result in negative returns.
            </Span>
        );
    }

    if (!tags) {
        return defaultDescription;
    }

    // Find the first matching description function
    for (const tag of tags) {
        const descriptionFn = LOOP_DESCRIPTIONS[tag];
        if (descriptionFn) {
            return descriptionFn(collateralToken, principalToken);
        }
    }

    return defaultDescription;
}

function getLeverageLoopDescription(
    collateralToken: TokenListMetadata,
    principalToken: TokenListMetadata,
    loopExpanded: LoopExpanded
): React.ReactElement | undefined {
    const direction = getLeverageLoopDirection(loopExpanded);

    if (direction === "Long") {
        return (
            <Span>
                This Loop gives leveraged long exposure to <TokenSolscanLink token={collateralToken} />, which follows
                the price of the underlying asset.
            </Span>
        );
    } else if (direction === "Short") {
        return (
            <Span>
                This Loop gives leveraged short exposure to <TokenSolscanLink token={principalToken} />, which follows
                the price of the underlying asset.
            </Span>
        );
    }
    return undefined;
}

/**
 * @description
 * This is a map of loop tags to their description.
 * The description is a function that takes in the collateral token, principal token, protocol, and secondary color.
 * The function returns a React element that is the description of the loop. Must live in the TSX file since we are rendering external link components.
 */
type LoopDescriptionFn = (
    collateralToken: TokenListMetadata,
    principalToken?: TokenListMetadata,
    protocol?: Protocol,
    secondary?: string
) => ReactElement;

export const LOOP_DESCRIPTIONS: Record<LoopFilterTags, LoopDescriptionFn> = {
    [LoopFilterTags.SolPegged]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a liquid staking token
            that earns staking rewards.
        </Span>
    ),

    [LoopFilterTags.Directional]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a directional position.
        </Span>
    ),

    [LoopFilterTags.Farming]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a farming strategy that
            amplifies rewards.
        </Span>
    ),

    [LoopFilterTags.StablecoinPegged]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a stablecoin strategy.
            Because both assets are dollar-linked, liquidation risk is low.
        </Span>
    ),

    [LoopFilterTags.DexLp]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a DEX liquidity position
            that earns trading fees.
        </Span>
    ),

    [LoopFilterTags.FixedYield]: (collateralToken, principalToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a fixed-yield token that
            redeems into <TokenSolscanLink token={principalToken} /> at maturity. Closing early may incur losses.
        </Span>
    ),

    [LoopFilterTags.Rwa]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a real world asset.
        </Span>
    ),

    [LoopFilterTags.Leverage]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, which follows the price
            of the underlying asset.
        </Span>
    ),

    [LoopFilterTags.CLMM]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a CLMM position that
            earns trading fees.
        </Span>
    ),

    [LoopFilterTags.FX]: (collateralToken, principalToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, tracking the price of an
            underlying asset in a foreign currency. The Loop is subject to FX risk: an increase in the underlying
            asset's price relative to <TokenSolscanLink token={principalToken} /> compounds returns, while a decrease
            can result in losses.
        </Span>
    )
};
